/* eslint-disable @typescript-eslint/no-explicit-any */
import { Accordion, AccordionItemState } from "react-accessible-accordion";
import { themeColors } from "@common/constants";
import AccordionItem from "@common/components/AccordionItem";
import { HiChevronDown } from "react-icons/hi";
import { FC, Fragment, ReactElement, useMemo } from "react";
import { Typography } from "@earned/core";
import { Transition } from "@headlessui/react";

interface AccordionListProps {
  data: any[];
  dataKey?: string;
  getData?: (item: any) => any;
  getHeaderContentClassName?: (item: any) => string;
  collapsedLabelKey: string;
  collapsedDataKey: string;
  expandedLabel: (item: any) => ReactElement | string;
  expandedData: (item: any) => string | number;
  colorClassNames?: string[];
}

const AccordionList = ({ data, ...rest }: AccordionListProps) => {
  return (
    <Accordion
      allowZeroExpanded
      allowMultipleExpanded
      className="border-primary"
    >
      {data.map((item, index) => (
        <Item
          key={`item:${item[rest.collapsedLabelKey]}}`}
          index={index}
          item={item}
          {...rest}
        />
      ))}
    </Accordion>
  );
};

interface AccordionItemProps {
  item: any;
  index: number;
  dataKey?: string;
  getData?: (item: any) => any;
  getHeaderContentClassName?: (item: any) => string;
  collapsedLabelKey: string;
  collapsedDataKey: string;
  expandedLabel: (item: any) => ReactElement | string;
  expandedData: (item: any) => string | number;
  colorClassNames?: string[];
}
const Item: FC<AccordionItemProps> = ({
  item,
  index,
  dataKey,
  getData,
  getHeaderContentClassName,
  collapsedLabelKey,
  collapsedDataKey,
  expandedLabel,
  expandedData,
  colorClassNames,
}) => {
  const isDisabled = useMemo(() => {
    return !getData || !getData(item);
  }, [getData, item]);
  return (
    <AccordionItem
      key={`root-${item[collapsedLabelKey]}:${index}`}
      headContent={({ isExpanded }) => (
        <div
          key={item[collapsedLabelKey]}
          className={
            "flex-row-1 w-100 flex min-h-[56px] flex-row items-center justify-between border-t border-primary py-5 " +
            (isDisabled ? "!cursor-default " : " ") +
            (getHeaderContentClassName ? getHeaderContentClassName(item) : "")
          }
        >
          <div className="flex flex-row items-center space-x-4 pr-5">
            {colorClassNames && (
              <div
                className={`ml-0.5 flex h-3.5 w-3.5 flex-none flex-row items-center justify-center rounded-full ${colorClassNames[index]}`}
              ></div>
            )}
            <Typography variant="body" color="primary" weight="medium">
              {item[collapsedLabelKey]}
            </Typography>
          </div>
          <div className="flex flex-row items-center">
            {collapsedDataKey && (
              <Typography variant="body" color="secondary">
                {item[collapsedDataKey]}
              </Typography>
            )}
            <div className="flex w-[54px] flex-row justify-end">
              {!isDisabled && (
                <HiChevronDown
                  size={24}
                  color={themeColors.green[5]}
                  className={
                    (isExpanded ? "rotate-180" : "") +
                    " mr-2 transition-all duration-300"
                  }
                />
              )}
            </div>
          </div>
        </div>
      )}
      content={
        <AccordionItemState>
          {({ expanded }) => (
            <Transition
              as={Fragment}
              show={expanded}
              enter="transition ease-in-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition ease-in-out duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="bg-green-40">
                {(dataKey ? item[dataKey] : getData ? getData(item) : [])?.map(
                  (data: any, subindex: number) => (
                    <div
                      key={`${item[collapsedLabelKey]}:${index}:${subindex}`}
                      className="flex min-h-10 flex-row items-start justify-between border-b border-white py-2.5 pl-5"
                    >
                      <div className="m-auto flex flex-1 flex-row items-center">
                        <Typography
                          variant="body-sm"
                          color="primary"
                          weight="medium"
                        >
                          {expandedLabel(data)}
                        </Typography>
                      </div>
                      <div className="flex flex-1 flex-none flex-row items-center justify-end pl-2 pr-[54px]">
                        <Typography
                          variant="body-sm"
                          color="secondary"
                          weight="semibold"
                        >
                          {expandedData(data)}
                        </Typography>
                      </div>
                    </div>
                  )
                )}
              </div>
            </Transition>
          )}
        </AccordionItemState>
      }
    />
  );
};
export default AccordionList;
